/**
 * Outlet Invoicing — Table Column Width Configuration
 * ------------------------------------------------------------
 * Edit this file to control column widths in the outlet invoicing order table
 * when `enhancedLayout` is enabled on CheckBoxSelectionTable.
 *
 * Column keys match the `name` field used in OrderList / AdvanceOutletInvoicing columns.
 * Use `checkbox` for the selection checkbox column.
 */

export const OUTLET_INVOICING_COLUMN_WIDTHS = {
  checkbox: {
    width: 42,
    minWidth: 42,
    maxWidth: 42,
    alignCenter: true,
  },
  index: {
    width: 52,
    minWidth: 52,
    maxWidth: 52,
    align: "center",
  },
  firmName: {
    width: 120,
    minWidth: 120,
    align: "left",
  },
  beatName: {
    width: 130,
    minWidth: 100,
    align: "left",
  },
  orderNo: {
    width: 115,
    minWidth: 100,
    align: "center",
  },
  orderDate: {
    width: 88,
    minWidth: 88,
    align: "center",
  },
  productName: {
    width: 140,
    minWidth: 140,
    align: "left",
  },
  outletUom: {
    width: 70,
    minWidth: 60,
    maxWidth: 80,
    align: "center",
  },
  rate: {
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    inputMaxWidth: 70,
    align: "right",
  },
  secondaryClosingQty: {
    width: 80,
    minWidth: 80,
    maxWidth: 80,
    headerLines: ["Existing", "Stock"],
    headerWrap: true,
    align: "right",
  },
  productStock: {
    width: 85,
    minWidth: 85,
    maxWidth: 85,
    headerLines: ["Remaining", "Stock"],
    headerWrap: true,
    align: "right",
  },
  billQty: {
    width: 68,
    minWidth: 68,
    maxWidth: 68,
    headerLines: ["Order", "Qty"],
    headerWrap: true,
    align: "right",
  },
  orderQty: {
    width: 75,
    minWidth: 75,
    inputMaxWidth: 70,
    align: "right",
  },
  total: {
    width: 80,
    minWidth: 70,
    maxWidth: 90,
    align: "right",
  },
};

const toCssSize = (value) =>
  typeof value === "number" ? `${value}px` : value;

const mapLegacyAlign = (alignCenter) => {
  if (alignCenter === "left") return "left";
  if (alignCenter === "right") return "right";
  return "center";
};

const mergeColumnWidthConfig = (columnKey, columnDef = {}) => {
  const base = OUTLET_INVOICING_COLUMN_WIDTHS[columnKey] || {};

  const legacyAlign =
    columnDef.align == null && columnDef.alignCenter != null
      ? mapLegacyAlign(columnDef.alignCenter)
      : null;

  return {
    ...base,
    ...(columnDef.width != null ? { width: columnDef.width } : {}),
    ...(columnDef.minWidth != null ? { minWidth: columnDef.minWidth } : {}),
    ...(columnDef.maxWidth != null ? { maxWidth: columnDef.maxWidth } : {}),
    ...(columnDef.inputMaxWidth != null
      ? { inputMaxWidth: columnDef.inputMaxWidth }
      : {}),
    ...(columnDef.headerTitle != null ? { headerTitle: columnDef.headerTitle } : {}),
    ...(columnDef.headerLines != null ? { headerLines: columnDef.headerLines } : {}),
    ...(columnDef.headerWrap != null ? { headerWrap: columnDef.headerWrap } : {}),
    ...(columnDef.align != null ? { align: columnDef.align } : {}),
    ...(legacyAlign != null ? { align: legacyAlign } : {}),
    ...(columnDef.alignCenter === true ? { alignCenter: true } : {}),
  };
};

const applyWidthSx = (cfg, sx) => {
  if (cfg.width != null) sx.width = toCssSize(cfg.width);
  if (cfg.minWidth != null) sx.minWidth = toCssSize(cfg.minWidth);
  if (cfg.maxWidth != null) sx.maxWidth = toCssSize(cfg.maxWidth);
};

const applyAlignCenterSx = (cfg, sx) => {
  if (cfg.alignCenter) {
    sx.textAlign = "center";
    sx.verticalAlign = "middle";
    sx.p = "8px 4px !important";
    sx["& .MuiCheckbox-root"] = {
      margin: "0 auto",
      display: "flex",
      justifyContent: "center",
    };
  }
};

export const getColumnWidthSx = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  const sx = {};

  applyWidthSx(cfg, sx);
  applyAlignCenterSx(cfg, sx);

  return sx;
};

export const getColumnHeaderSx = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  const sx = {};

  applyWidthSx(cfg, sx);
  applyAlignCenterSx(cfg, sx);

  if (cfg.headerWrap) {
    sx.whiteSpace = "normal";
    sx.lineHeight = 1.25;
    sx.fontSize = "10px";
    sx.textAlign = "center";
    sx.verticalAlign = "middle";
    sx.wordBreak = "break-word";
  }

  return sx;
};

export const getColumnAlign = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  if (cfg.alignCenter) return "center";
  return cfg.align || "left";
};

export const getColumnHeaderAlign = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  if (cfg.headerWrap || cfg.alignCenter) return "center";
  return cfg.align || "left";
};

export const getColumnHeaderClassName = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  return cfg.headerWrap ? "header-wrap-cell" : "";
};

export const getColumnHeaderLines = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  return cfg.headerLines?.length ? cfg.headerLines : null;
};

export const getColumnHeaderTitle = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);
  return cfg.headerTitle || columnDef.title || "";
};

export const getColumnInputStyle = (columnKey, columnDef = {}) => {
  const cfg = mergeColumnWidthConfig(columnKey, columnDef);

  if (cfg.inputMaxWidth == null) return {};

  return { maxWidth: toCssSize(cfg.inputMaxWidth) };
};
